import React, { useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSupabase } from '../contexts/SupabaseContext';
import AdminNavBar from '../components/AdminNavBar';
import { Submission } from '../lib/supabaseClient';
import withIdleTimer from '../hoc/withIdleTimer';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading, sessionExpired } = useAuth();
  const [submissions, setSubmissions] = React.useState<Submission[]>([]);
  const [allSubmissions, setAllSubmissions] = React.useState<Submission[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [isLoading, setIsLoading] = React.useState(true);
  
  // Add refs to track state and prevent duplicate operations
  const isFetchingRef = useRef(false);
  const userIdRef = useRef<string | null>(null);
  
  // Memoize the Supabase client
  const supabase = useSupabase();

  // Handle session expiration
  React.useEffect(() => {
    if (sessionExpired) {
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);

  // Memoize fetchSubmissions to prevent recreation on each render
  const fetchSubmissions = useCallback(async () => {
    // Skip if already fetching or no user
    if (isFetchingRef.current || !user) {
      return;
    }
    
    isFetchingRef.current = true;
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('submissions')
        .select(`*`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching submissions:', error);
        return;
      }

      setAllSubmissions((data || []) as Submission[]);
      setSubmissions((data || []) as Submission[]);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [supabase, user]);

  // Fetch submissions when auth is ready
  React.useEffect(() => {
    // Update userIdRef if user changes
    if (user?.id !== userIdRef.current) {
      userIdRef.current = user?.id || null;
    }
    
    // Skip if auth is still loading
    if (authLoading) {
      return;
    }
    
    if (!user) {
      setIsLoading(false);
      return;
    }

    fetchSubmissions();
  }, [user?.id, authLoading, fetchSubmissions]); // Add user?.id instead of user to avoid re-renders

  // Apply filter when statusFilter changes
  React.useEffect(() => {
    if (statusFilter === "all") {
      setSubmissions(allSubmissions);
    } else {
      const filtered = allSubmissions.filter(
        submission => submission.status === statusFilter
      );
      setSubmissions(filtered);
    }
  }, [statusFilter, allSubmissions]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      DRAFT: 'bg-gray-200',
      SUBMISSION: 'bg-blue-200',
      ADMINISTRATIVE_REVIEW: 'bg-yellow-200',
      IRB_REVIEW: 'bg-purple-200',
      DECISION_ISSUED: 'bg-green-200'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-200';
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Show loading state during auth loading or data fetching
  if (authLoading || isLoading) {
    return (
      <div className="flex flex-col h-screen">
        <AdminNavBar user={user} onSignOut={handleLogout} />
        <div className="flex-1 bg-gray-50 mt-16">
          <div className="p-8 text-center">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
              <span className="ml-2 text-gray-600">Loading submissions...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <AdminNavBar user={user} onSignOut={handleLogout} />

      {/* Main Content - Adjusted for top navbar */}
      <div className="flex-1 bg-gray-50 mt-16">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold">All Submissions</h1>
            <div className="flex space-x-4">
              <select 
                className="border rounded-lg px-4 py-2"
                value={statusFilter}
                onChange={handleFilterChange}
              >
                <option value="all">All Statuses</option>
                <option value="DRAFT">Draft</option>
                <option value="NEEDS_ACTION">Needs Action</option>
                <option value="IRB_REVIEW">IRB Review</option>
                <option value="DECISION_ISSUED">Decision Issued</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            {submissions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No submissions to review.
              </div>
            ) : (
              <ul className="divide-y">
                {submissions.map((submission) => (
                  <li key={submission.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">
                          {submission.data && typeof submission.data === 'object' && 'protocol_title' in submission.data 
                            ? (submission.data as any).protocol_title 
                            : 'N/A'}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {submission.data && typeof submission.data === 'object' && 'sponsor_name' in submission.data
                            ? (submission.data as any).sponsor_name
                            : 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(submission.status)}`}>
                          {submission.status.replace(/_/g, ' ')}
                        </span>
                        <button
                          onClick={() => navigate(`/admin/submissions/${submission.id}`)}
                          className="text-[#F55D3E] hover:text-[#F9A594]"
                        >
                          Review →
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withIdleTimer(AdminDashboard); 